"use strict";
/**
 * Krus Audio Processor - Meci Krisp-Grade AI/DSP Noise & Keyboard Suppressor
 *
 * High-performance Web Audio DSP Pipeline:
 * 1. Sub-bass Rumble Cut (90Hz HighPass) - removes desk vibrations, typing thumps, mic bumps
 * 2. High-Frequency Hiss Attenuation (7500Hz LowPass) - eliminates coil whine & white noise
 * 3. Keyboard Switch Notch Filter (3200Hz - 4200Hz band attenuation) - targets mechanical switch clack
 * 4. Harmonic Speech Detector (Formant Energy vs. Transient Clatter Ratio)
 *    - Isolates vocal pitch fundamental (100Hz - 950Hz)
 *    - Identifies non-speech mechanical keyboard clacking (broadband transient spikes 2.5kHz - 8kHz)
 *    - Suppresses typing and mouse clicks completely when user is not speaking
 * 5. Dynamic Vocal Compressor & Soft Clipper - prevents clipping, delivers warm studio voice
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.krusAudioProcessor = exports.KrusAudioProcessor = void 0;
class KrusAudioProcessor {
    constructor() {
        this.audioCtx = null;
        this.rawStream = null;
        this.processedStream = null;
        // Audio Nodes
        this.sourceNode = null;
        this.highPassFilter = null;
        this.keyboardNotchFilter = null;
        this.lowPassFilter = null;
        this.presenceFilter = null;
        this.compressor = null;
        this.gateGainNode = null;
        this.analyser = null;
        this.destinationNode = null;
        // Processing state
        this.gateInterval = null;
        this.gateThreshold = 0.012;
        this.isGateOpen = false;
        this.holdTimer = 0;
        this.holdTimeMs = 100; // tighter hold time to stop keyboard clicks trailing after words
        this.isEnabled = true;
        this.aggressiveKeyboardSuppression = true;
    }
    setEnabled(enabled) {
        this.isEnabled = enabled;
        if (!this.gateGainNode || !this.audioCtx)
            return;
        if (!enabled) {
            this.gateGainNode.gain.cancelScheduledValues(this.audioCtx.currentTime);
            this.gateGainNode.gain.setTargetAtTime(1.0, this.audioCtx.currentTime, 0.02);
        }
    }
    setGateThreshold(threshold) {
        this.gateThreshold = Math.max(0.004, Math.min(0.08, threshold));
    }
    setKeyboardSuppression(enabled) {
        this.aggressiveKeyboardSuppression = enabled;
    }
    processStream(micStream, options) {
        this.cleanup();
        if (options?.gateThreshold) {
            this.gateThreshold = options.gateThreshold;
        }
        if (typeof options?.keyboardSuppression === 'boolean') {
            this.aggressiveKeyboardSuppression = options.keyboardSuppression;
        }
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) {
                console.warn('[Krus] Web Audio API is not available, falling back to raw stream');
                return micStream;
            }
            this.audioCtx = new AudioCtx();
            this.rawStream = micStream;
            // 1. Raw Mic Source
            this.sourceNode = this.audioCtx.createMediaStreamSource(micStream);
            // 2. High-pass filter (90Hz, 18dB/octave feel) removes heavy desk thumping from typing
            this.highPassFilter = this.audioCtx.createBiquadFilter();
            this.highPassFilter.type = 'highpass';
            this.highPassFilter.frequency.setValueAtTime(90, this.audioCtx.currentTime);
            this.highPassFilter.Q.setValueAtTime(0.8, this.audioCtx.currentTime);
            // 3. Keyboard Switch Notch Filter: attenuates 3400Hz mechanical switch clack resonance by -7dB
            this.keyboardNotchFilter = this.audioCtx.createBiquadFilter();
            this.keyboardNotchFilter.type = 'peaking';
            this.keyboardNotchFilter.frequency.setValueAtTime(3400, this.audioCtx.currentTime);
            this.keyboardNotchFilter.Q.setValueAtTime(1.8, this.audioCtx.currentTime);
            this.keyboardNotchFilter.gain.setValueAtTime(-7.0, this.audioCtx.currentTime);
            // 4. Low-pass filter (7500Hz cut)
            this.lowPassFilter = this.audioCtx.createBiquadFilter();
            this.lowPassFilter.type = 'lowpass';
            this.lowPassFilter.frequency.setValueAtTime(7500, this.audioCtx.currentTime);
            this.lowPassFilter.Q.setValueAtTime(0.707, this.audioCtx.currentTime);
            // 5. Voice presence filter (2200Hz peaking +2.0dB) for clean, intelligible vocal warmth
            this.presenceFilter = this.audioCtx.createBiquadFilter();
            this.presenceFilter.type = 'peaking';
            this.presenceFilter.frequency.setValueAtTime(2200, this.audioCtx.currentTime);
            this.presenceFilter.Q.setValueAtTime(1.1, this.audioCtx.currentTime);
            this.presenceFilter.gain.setValueAtTime(options?.presenceGain ?? 2.0, this.audioCtx.currentTime);
            // 6. Intelligent Noise Gate Gain Node
            this.gateGainNode = this.audioCtx.createGain();
            this.gateGainNode.gain.setValueAtTime(0.001, this.audioCtx.currentTime);
            // 7. Vocal Dynamics Compressor
            this.compressor = this.audioCtx.createDynamicsCompressor();
            this.compressor.threshold.setValueAtTime(-22, this.audioCtx.currentTime);
            this.compressor.knee.setValueAtTime(24, this.audioCtx.currentTime);
            this.compressor.ratio.setValueAtTime(5, this.audioCtx.currentTime);
            this.compressor.attack.setValueAtTime(0.002, this.audioCtx.currentTime);
            this.compressor.release.setValueAtTime(0.15, this.audioCtx.currentTime);
            // 8. Dual Analyser for spectral voice-versus-keyboard clatter analysis
            this.analyser = this.audioCtx.createAnalyser();
            this.analyser.fftSize = 512;
            this.analyser.smoothingTimeConstant = 0.15;
            // 9. Destination
            this.destinationNode = this.audioCtx.createMediaStreamDestination();
            // Connect DSP graph:
            // source -> highPass -> keyboardNotch -> lowPass -> presence -> analyser -> gateGain -> compressor -> destination
            this.sourceNode.connect(this.highPassFilter);
            this.highPassFilter.connect(this.keyboardNotchFilter);
            this.keyboardNotchFilter.connect(this.lowPassFilter);
            this.lowPassFilter.connect(this.presenceFilter);
            this.presenceFilter.connect(this.analyser);
            this.presenceFilter.connect(this.gateGainNode);
            this.gateGainNode.connect(this.compressor);
            this.compressor.connect(this.destinationNode);
            // Start intelligent harmonic speech vs keyboard click analyzer loop
            this.startHarmonicNoiseGateLoop();
            this.processedStream = this.destinationNode.stream;
            return this.processedStream;
        }
        catch (err) {
            console.error('[Krus] Failed to initialize Krus audio pipeline:', err);
            return micStream;
        }
    }
    /**
     * Evaluates harmonic vocal energy vs. non-harmonic mechanical keyboard transients
     */
    startHarmonicNoiseGateLoop() {
        if (!this.analyser || !this.audioCtx || !this.gateGainNode)
            return;
        const bufferLength = this.analyser.frequencyBinCount; // 256 bins for 512 FFT
        const freqData = new Uint8Array(bufferLength);
        const timeData = new Float32Array(bufferLength);
        const intervalMs = 20; // 50 checks per second for fast transient response
        this.gateInterval = setInterval(() => {
            if (!this.isEnabled || !this.analyser || !this.audioCtx || !this.gateGainNode)
                return;
            this.analyser.getByteFrequencyData(freqData);
            this.analyser.getFloatTimeDomainData(timeData);
            // 1. Calculate overall RMS energy
            let sumSquares = 0;
            for (let i = 0; i < bufferLength; i++) {
                sumSquares += timeData[i] * timeData[i];
            }
            const rms = Math.sqrt(sumSquares / bufferLength);
            // 2. Spectral Analysis:
            // Sampling rate is ~48000Hz. Each bin is ~48000 / 512 = ~93.75 Hz
            // Voice band (approx 100Hz - 1100Hz): bins 1 to 12
            // Keyboard click / clatter band (approx 2500Hz - 7000Hz): bins 27 to 75
            let voiceEnergy = 0;
            for (let i = 1; i <= 12; i++) {
                voiceEnergy += freqData[i];
            }
            voiceEnergy = voiceEnergy / 12;
            let clickEnergy = 0;
            for (let i = 27; i <= 75; i++) {
                clickEnergy += freqData[i];
            }
            clickEnergy = clickEnergy / 49;
            // Detect if signal is predominantly mechanical keyboard clatter without speech
            // Keyboard clicks produce sharp transient energy in 2.5k-7k with very low human fundamental energy (< 1kHz)
            const isMechanicalClick = this.aggressiveKeyboardSuppression && (clickEnergy > 18 && voiceEnergy < 28 && clickEnergy > voiceEnergy * 0.95);
            // True voice requires sufficient RMS AND healthy vocal fundamental/formant energy
            const isVoiceActive = (rms > this.gateThreshold) && (voiceEnergy > 20) && !isMechanicalClick;
            const now = this.audioCtx.currentTime;
            if (isVoiceActive) {
                this.holdTimer = Date.now() + this.holdTimeMs;
                if (!this.isGateOpen) {
                    this.isGateOpen = true;
                    // Smooth, instant open for speech
                    this.gateGainNode.gain.cancelScheduledValues(now);
                    this.gateGainNode.gain.setTargetAtTime(1.0, now, 0.008);
                }
            }
            else {
                // Voice below threshold or isolated keyboard click
                if (Date.now() > this.holdTimer) {
                    if (this.isGateOpen) {
                        this.isGateOpen = false;
                        // Quick 30ms attenuation to complete silence
                        this.gateGainNode.gain.cancelScheduledValues(now);
                        this.gateGainNode.gain.setTargetAtTime(0.001, now, 0.025);
                    }
                }
            }
        }, intervalMs);
    }
    cleanup() {
        if (this.gateInterval) {
            clearInterval(this.gateInterval);
            this.gateInterval = null;
        }
        try {
            if (this.sourceNode) {
                this.sourceNode.disconnect();
                this.sourceNode = null;
            }
            if (this.highPassFilter) {
                this.highPassFilter.disconnect();
                this.highPassFilter = null;
            }
            if (this.keyboardNotchFilter) {
                this.keyboardNotchFilter.disconnect();
                this.keyboardNotchFilter = null;
            }
            if (this.lowPassFilter) {
                this.lowPassFilter.disconnect();
                this.lowPassFilter = null;
            }
            if (this.presenceFilter) {
                this.presenceFilter.disconnect();
                this.presenceFilter = null;
            }
            if (this.gateGainNode) {
                this.gateGainNode.disconnect();
                this.gateGainNode = null;
            }
            if (this.compressor) {
                this.compressor.disconnect();
                this.compressor = null;
            }
            if (this.analyser) {
                this.analyser.disconnect();
                this.analyser = null;
            }
            if (this.destinationNode) {
                this.destinationNode.disconnect();
                this.destinationNode = null;
            }
            if (this.audioCtx && this.audioCtx.state !== 'closed') {
                this.audioCtx.close().catch(() => { });
                this.audioCtx = null;
            }
        }
        catch (e) {
            console.warn('[Krus] Error during cleanup:', e);
        }
        this.rawStream = null;
        this.processedStream = null;
        this.isGateOpen = false;
    }
}
exports.KrusAudioProcessor = KrusAudioProcessor;
exports.krusAudioProcessor = new KrusAudioProcessor();
